/**
 * TanStack Query hooks for the Network (Cato SASE) board.
 *
 * The backend serves these routes from a TTL-cached snapshot because Cato's
 * `accountMetrics` query allows only 15 calls a minute for the whole account —
 * so polling harder here buys nothing, and the refetch intervals below simply
 * track the backend's own poll interval.
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching `src/lib/api/hooks.ts`: the barrel re-exports
 * these hooks, so importing it here would close an import cycle.
 */

import { queryOptions, useQuery } from "@tanstack/react-query";

import { getNetworkLinks, getNetworkOverview, getNetworkSites } from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the board's main payload. */
export const NETWORK_OVERVIEW_KEY = ["network", "overview"] as const;
/** Query key for the per-site detail route. */
export const NETWORK_SITES_KEY = ["network", "sites"] as const;
/** Query key for the flattened WAN-port list. */
export const NETWORK_LINKS_KEY = ["network", "links"] as const;

/**
 * How often the board refetches, in ms. The backend refreshes its Cato snapshot
 * once a minute, so anything faster just re-reads the same cached bytes.
 */
const REFETCH_INTERVAL_MS = 60_000;

/**
 * The board's entry query, as options rather than a hook.
 *
 * Split out so `prefetchColdBoards` can warm it without rendering the board —
 * one definition, so the prefetched entry can never drift from the one the
 * component subscribes to and silently miss the cache.
 */
export function networkOverviewQuery() {
  return queryOptions({
    queryKey: NETWORK_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getNetworkOverview();
      logger.debug("[network] overview received", {
        configured: data?.configured,
        sites: data?.sites?.length ?? 0,
      });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

/** The SASE estate: config state, rollup, sites, and recent connectivity events. */
export function useNetworkOverview() {
  return useQuery(networkOverviewQuery());
}

/**
 * Per-site detail on its own key.
 *
 * @param enabled Skip the request entirely while Cato is unconfigured — the
 *   answer is a known-empty payload and the board already has it.
 */
export function useNetworkSites(enabled = true) {
  return useQuery({
    queryKey: NETWORK_SITES_KEY,
    queryFn: async () => {
      const { data } = await getNetworkSites();
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
    enabled,
  });
}

/**
 * Every last-mile WAN port in the estate, flattened for the link matrix.
 *
 * @param enabled Skip the request while Cato is unconfigured.
 */
export function useNetworkLinks(enabled = true) {
  return useQuery({
    queryKey: NETWORK_LINKS_KEY,
    queryFn: async () => {
      const { data } = await getNetworkLinks();
      logger.debug("[network] links received", data?.links?.length ?? 0);
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
    enabled,
  });
}
